import { FormControl, FormErrorMessage } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import {
  Input,
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  Paragraph,
} from 'ui-components';

import { useVideoUrlModalStore } from './useVideoUrlModalStore';
import { validateVideoUrl } from './utils';

export const VideoUrlModal = () => {
  const { isOpen, reset, mode, callback, initialUrl } = useVideoUrlModalStore();
  const [url, setUrl] = useState(initialUrl || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
    };
  }, []);

  useEffect(() => {
    setUrl(initialUrl || '');
  }, [initialUrl]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setUrl('');
      setError('');
      setIsLoading(false);
      setIsValidUrl(false);
    }
  }, [isOpen]);

  // Validate URL when debounced value changes
  useEffect(() => {
    const validateUrl = async () => {
      if (!url) {
        setError('');
        setIsValidUrl(false);
        return;
      }

      setIsLoading(true);
      try {
        const validation = await validateVideoUrl(url);
        setError(validation.error || '');
        setIsValidUrl(validation.isValid);
      } catch (error) {
        setError('An error occurred while validating the video URL');
        setIsValidUrl(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateUrl();
  }, [url]);

  const closeModal = useCallback(() => {
    reset();
  }, [reset]);

  const handleSave = useCallback(async () => {
    if (!isValidUrl) return;
    callback(url);
    closeModal();
  }, [url, isValidUrl, callback]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value.trim();
      setUrl(newUrl);

      // Clear error immediately when user starts typing
      if (error) {
        setError('');
      }
    },
    [error],
  );

  return (
    <Modal isOpen={isOpen} onClose={closeModal}>
      <ModalHeaderDefault title={mode === 'add' ? 'Add video' : 'Edit video'} />
      <ModalBody>
        <FormControl isInvalid={!!error}>
          <div className="flex flex-col gap-1">
            <Input
              value={url}
              onChange={handleChange}
              // @ts-ignore :|
              onClear={() => handleChange({ target: { value: '' } })}
              placeholder="Enter video URL"
              isDisabled={isLoading}
              autoFocus
              trackId="video-url-input"
              data-testid="video-url-input"
            />
            <div className="px-4">
              {error ? (
                <FormErrorMessage
                  whiteSpace="pre-line"
                  data-testid="video-url-error"
                >
                  {error}
                </FormErrorMessage>
              ) : (
                <Paragraph size="md" color="primary.300">
                  Supported videos: YouTube, Vimeo, Google Drive, Instagram and
                  TikTok.
                </Paragraph>
              )}
            </div>
          </div>
        </FormControl>
      </ModalBody>

      <ModalFooterDefault
        secondary={{
          ariaLabel: 'cancel',
          onClick: closeModal,
          isDisabled: isLoading,
          title: 'Cancel',
          trackId: 'add-video-modal-cancel-btn',
        }}
        primary={{
          ariaLabel: 'Save',
          onClick: handleSave,
          isLoading: isLoading,
          isDisabled: !isValidUrl,
          title: 'Save',
          trackId: 'add-video-modal-save-btn',
        }}
      />
    </Modal>
  );
};
